/* jshint esversion: 6, -W097 */
/* globals module */
"use strict";

/**
 * Renders a rebate provider statement from CMS
 *
 * <rebate-provider-statement data-rebate="rebate"></rebate-provider-statement>
 *
 * @module enervee_utils.rebates
 */
function rebateProviderStatementConfig() {
    return {
        restrict: "E",
        scope: {
            rebate: "=",
        },
        template: '<span ng-bind-html="rebateStatement | trust"></span>',
        controller: rebateProviderStatementCtrl,
    };
}

rebateProviderStatementCtrl.$inject = [
    "$scope",
    "$filter",
    "$q",
    "$timeout",
    "CmsContentService",
    "UtilService",
];
function rebateProviderStatementCtrl(
    $scope,
    $filter,
    $q,
    $timeout,
    CmsContentService,
    UtilService,
) {
    // Update DOM with retrieved rebate statement post digest
    $timeout(updateRebateStatement);

    $scope.$watch("rebate", function (newRebate, oldRebate) {
        // Only run if the rebate has changed
        if (oldRebate !== newRebate) {
            updateRebateStatement();
        }
    });

    /**
     * Updates the rebateStatement variable in the $scope to either a statement
     * pulled from the CMS or an empty string.
     */
    function updateRebateStatement() {
        if ($scope.rebate === null) return;

        const rebateStatementRequest = UtilService.isFeatureEnabled("custom_rebate_statement")
            ? fetchCustomRebateStatement($scope.rebate)
            : fetchProviderStatement($scope.rebate);

        rebateStatementRequest
            // Default to an empty string in case there was an error.
            .catch(() => "")
            .then((text) => ($scope.rebateStatement = text));
    }

    /**
     * Fetches and formats the text for the Custom Rebate Statement feature, returning
     * a promise that resolves to the final text for the statement or rejects with
     * an error from the CMS.
     *
     * @param {Object} rebate - The rebate that we are retrieving text for.
     * @returns {Promise} A promise that resolves to the formatted statement or rejects
     *                    to an error object.
     */
    function fetchCustomRebateStatement(rebate) {
        return CmsContentService.getMarketPlaceText(
            "base:custom_rebate_statement",
            {},
            rebate.category_id,
        ).catch((error) =>
            $q.reject({
                detail: "Could not retrieve custom rebate statement.",
                source: "fetchCustomRebateStatement",
                cause: error,
            }),
        );
    }

    /**
     * Fetches and formats the text for the standard provider statement, returning
     * a promise that resolves to the final text for the statement or rejects with
     * an error object.
     *
     * @param {Object} rebate - The rebate that we are retrieving text for.
     * @returns {Promise} A promise that resolves to the formatted statement or rejects
     *                    to an error object.
     */
    function fetchProviderStatement(rebate) {
        const allRequests = [
            retrieveCmsDateRangeText(rebate),
            // It doesn't matter if the provider segment fails,
            // we can fallback to an empty string.
            retrieveCmsProviderText(rebate).catch(() => ""),
        ];

        return $q
            .all(allRequests)
            .then(
                // Remove falsy values and concatenate with <br> tags
                (textFragments) => textFragments.filter(Boolean).join("<br>"),
            )
            .catch((error) =>
                $q.reject({
                    detail: "Could not fetch provider statement",
                    source: "fetchProviderStatement",
                    cause: error,
                }),
            );
    }

    /**
     * Retrieves the correct date range text for the provided rebate from the CMS,
     * returning a promise that resolves to text or rejects to an error.
     *
     * @param {Object} rebate - The rebate that we are retrieving text for.
     * @returns {Promise} A promise that resolves to the formatted statement.
     */
    function retrieveCmsDateRangeText(rebate) {
        let cmsTextKey;
        if (rebate.start_date && rebate.end_date) {
            cmsTextKey = "base:rebate_valid_date_range_statement";
        } else if (rebate.start_date && !rebate.end_date) {
            cmsTextKey = "base:rebate_valid_from_statement";
        } else if (!rebate.start_date && rebate.end_date) {
            cmsTextKey = "base:rebate_valid_until_statement";
        }

        if (!cmsTextKey) {
            return $q.reject(
                "A valid cmsTextKey could not be found for a rebate without a start and end date.",
            );
        }

        return CmsContentService.getMarketPlaceText(
            cmsTextKey,
            {
                start_date: $filter("date")(rebate.start_date, "longDate"),
                end_date: $filter("date")(rebate.end_date, "longDate"),
            },
            rebate.category_id,
        );
    }

    /**
     * Retrieves the provider statement for the provided rebate, returning a promise
     * that resolves to the provider text or rejects to an error.
     *
     * @param {Object} rebate - The rebate that we are retrieving text for.
     * @returns {Promise} A promise that resolves to the provider text.
     */
    function retrieveCmsProviderText(rebate) {
        if (!rebate.provider_name) {
            return $q.reject("The rebate does not have a provider_name configured.");
        }

        return CmsContentService.getMarketPlaceText(
            "base:rebate_provider_statement",
            { provider_name: rebate.provider_name },
            rebate.category_id,
        );
    }
}

module.exports = rebateProviderStatementConfig;
